/**
 * Brand identity. The one place a name or a logo path is written down.
 *
 * The resale requirement means no component may contain the string "OriTech" or a path to a
 * logo file. Both apps read these, and both bind the logo to a CSS variable so a stylesheet can
 * override it without a rebuild.
 */

export const brand = {
  name: 'OriTech',
  /** Shown under the logo on the login screen and in the receipt footer. */
  tagline: 'Point of Sale',
  /** Window title suffix. */
  productName: 'OriTech POS',
} as const;

/**
 * What the apps wear before any shop is known: the sign-in screen on a device that has never
 * signed in, every device the moment it signs out, and the browser tab behind both.
 *
 * Separate from `brand` because `brand` is the *product's* identity and the product is named
 * after its first client. On a screen that belongs to no shop, "OriTech" is not a neutral
 * default — it is one shop's name over every other shop's door, which is the same leak the
 * neutral colour look exists to close. These strings name the app, not a company.
 *
 * The copyright line is not here and should not be: that names the vendor, which is a legal
 * statement about who wrote the software rather than a claim about whose shop this is.
 */
export const neutralBrand = {
  /** The POS tab title, and the name on its sign-in screen. */
  pos: brand.tagline,
  /** The dashboard's equivalent. */
  dashboard: 'Dashboard',
  /** The generic shopfront, matching `ShopMark`'s fallback glyph. Never a shop's artwork. */
  mark: '/brand/generic-mark.svg',
} as const;

/**
 * Logo asset paths, resolved from each app's `public/` root.
 *
 * The mark and the wordmark are separate files on purpose: the POS header has room for the mark
 * alone, and a wordmark scaled down to 24px is illegible mush.
 */
export const logo = {
  /** The circular O/T mark, no text. Header, favicon, app icon. */
  mark: '/brand/oritech-mark.svg',
  /** Mark plus wordmark, horizontal. Login screen, dashboard rail. */
  lockup: '/brand/oritech-lockup.svg',
  /** Light-on-dark variant, for dark theme and the inverse surface. */
  lockupInverse: '/brand/oritech-lockup-inverse.svg',
  /** Receipt header. Monochrome, because thermal printers have no colour. */
  receipt: '/brand/oritech-receipt.svg',
} as const;

/**
 * Bound as CSS variables so a themed deployment can point them elsewhere without touching a
 * component. Emitted alongside the colour tokens.
 */
export const logoVariables = {
  '--pos-logo-mark': `url('${logo.mark}')`,
  '--pos-logo-lockup': `url('${logo.lockup}')`,
  '--pos-logo-lockup-inverse': `url('${logo.lockupInverse}')`,
} as const;

/**
 * The source artwork is a raster JPG on a cream background — usable as a reference, not as an
 * asset. It needs vectorising before step 4, or the mark will show a cream box on the dark theme
 * and blur on a hi-dpi phone. Kept here so the trail from artwork to asset is not lost.
 */
export const SOURCE_ARTWORK = '../assets/oritech-logo-source.jpg';
